import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay/legacy';
import { getQueryParam } from '../../../utils/uriUtils';
import { SharedModifyPortfolioItems } from '../../../mutations/FindingSharedModifyPortfolioItems';
import { SharedParallelizedQueryRenderer } from '../../../shared/SharedParallelizedQueryRenderer';
import { locations, trackHeartFavorited } from '../../../utils/tracking/shared/favoritesTracking';
import {
    getForceFavoriteItemId,
    clearForceFavoriteItemId,
    FORCE_FAVORITE_ITEM_ID,
} from './localStorageHelpers';

export class FavoritesProvider extends Component {
    constructor(props) {
        super(props);

        this.state = {
            variables: {
                userIds: [],
                selectedItemIds: [],
                loadPortfolioData: false,
                shouldFillInFolder: false,
                shouldShowHeartCount: false,
            },
        };
    }

    componentDidMount() {
        if (this.shouldPerformAutoFavorite()) {
            this.handleAutoFavorite();
        } else if (this.props.userId) {
            // Set refetch variables if user is logged in
            this.setVariables();
        } else if (this.props.itemIds.length && this.props.shouldShowHeartCount) {
            // Even if user is not logged in, refetch heart count data
            this.setState(state => ({
                variables: {
                    ...state.variables,
                    selectedItemIds: this.props.itemIds,
                    shouldShowHeartCount: true,
                },
            }));
        }
    }

    componentDidUpdate(prevProps) {
        if (this.shouldPerformAutoFavorite(prevProps)) {
            this.handleAutoFavorite();
        } else if (
            (!prevProps.userId && this.props.userId) ||
            (this.props.userId && prevProps.itemIds.toString() !== this.props.itemIds.toString())
        ) {
            this.setVariables();
        }

        const forceFavoriteItemId = getForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID);

        if (this.props.itemIds.includes(forceFavoriteItemId) && this.props.userId) {
            this.handleAutoFavorite(forceFavoriteItemId);
            clearForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID);
        }

        if (!prevProps.shouldRefetch && this.props.shouldRefetch) {
            this.setVariables();
        }
    }

    shouldPerformAutoFavorite(prevProps = {}) {
        const hasAutoFavoriteQueryParam =
            getQueryParam(location.href, 'follow') === 'item' ||
            getQueryParam(location.href, 'save') === 'item';
        return (
            this.props.shouldAutoFavorite &&
            hasAutoFavoriteQueryParam &&
            !prevProps.userId &&
            this.props.userId
        );
    }

    handleAutoFavorite(favoriteItemId) {
        const { itemIds, environment, userId } = this.props;
        const itemId = favoriteItemId || itemIds[0];

        SharedModifyPortfolioItems.commit(environment, {
            action: 'ADD',
            itemId,
            portfolioType: 'HEART',
            userId,
            onCompleted: () => {
                trackHeartFavorited({ location: locations.AUTO_EMAIL, itemPk: itemId });
                this.setVariables();
            },
        });
    }

    setVariables() {
        const { userId, itemIds, shouldShowHeartCount } = this.props;
        this.setState({
            variables: {
                userIds: [userId],
                selectedItemIds: itemIds,
                loadPortfolioData: true,
                shouldFillInFolder: true,
                shouldShowHeartCount: !!shouldShowHeartCount,
            },
        });
    }

    render() {
        const { environment } = this.props;

        return (
            <SharedParallelizedQueryRenderer
                query={graphql`
                    query FavoritesProviderQuery(
                        $loadPortfolioData: Boolean = false
                        $selectedItemIds: [String] = []
                        $userIds: [String] = []
                        $shouldFillInFolder: Boolean = false
                        $shouldShowHeartCount: Boolean = false
                    ) {
                        viewer {
                            ...FolderWrapper_viewer
                                @arguments(
                                    selectedItemIds: $selectedItemIds
                                    shouldFillInFolder: $shouldFillInFolder
                                    userIds: $userIds
                                )
                            ...HeartWrapper_viewer
                                @arguments(
                                    loadPortfolioData: $loadPortfolioData
                                    selectedItemIds: $selectedItemIds
                                    userIds: $userIds
                                    showHeartCount: $shouldShowHeartCount
                                )
                        }
                    }
                `}
                environment={environment}
                variables={this.state.variables}
                render={({ data }) => {
                    const { viewer = null } = data || {};
                    const { userId, itemIds, disable } = this.props;
                    const userIds = userId ? [userId] : [];

                    const childrenProps = {
                        userId,
                        itemIds,
                        userIds,
                        disable,
                        location: this.props.location,
                        fetchHeart: this.props.fetchHeart,
                        fetchFolder: this.props.fetchFolder,
                        singlePortfolioItem: true,
                        loadPortfolioItemData: true,
                        viewer,
                    };

                    return this.props.children({ props: childrenProps });
                }}
            />
        );
    }
}

FavoritesProvider.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
    shouldAutoFavorite: false,
    shouldRefetch: false,
};

FavoritesProvider.propTypes = {
    children: PropTypes.func,
    disable: PropTypes.bool,
    environment: PropTypes.object.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    fetchHeart: PropTypes.bool.isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    location: PropTypes.oneOf([...Object.keys(locations)]).isRequired,
    shouldAutoFavorite: PropTypes.bool,
    userId: PropTypes.string,
    shouldRefetch: PropTypes.bool,
    shouldShowHeartCount: PropTypes.bool,
};
